(function () {
  'use strict';

  // Product photos are square canvases with varying amounts of transparent
  // padding around the module itself (a tall candle uses most of the frame,
  // a flat holder uses very little). To stack them without visible gaps we
  // measure where each image's actual content starts/ends and pull the next
  // layer up to close that gap. Falls back to no trimming if canvas reads
  // are blocked (e.g. CORS), so layers just sit flush instead of erroring.
  var contentBoundsCache = {};

  // Each module has a small connector "foot" that sinks into the socket of
  // the piece below it (that's how the real candles click together). The
  // content-bounds overlap alone only makes layers touch; this extra pull
  // hides the foot behind the piece underneath, matching the physical joint.
  var EXTRA_INTERLOCK_RATIO = 0.08;

  // Homepage-to-full-builder handoff: when a shopper picks modules in the
  // compact homepage preview and clicks through to the full configurator,
  // we stash their in-progress build in localStorage (keyed by variant id,
  // since variant ids are the same across every page on this store) and
  // the full-page section picks it up on load. TTL keeps a stale build from
  // reappearing days later; consuming (removing) it on read stops it from
  // re-applying on a back-button return visit.
  var HANDOFF_KEY = 'candlStackHandoff';
  var HANDOFF_TTL_MS = 10 * 60 * 1000;
  var handoffConsumed = false;

  function getContentBounds(src) {
    if (contentBoundsCache[src]) return contentBoundsCache[src];
    var promise = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var sampleSize = 80;
          var ratio = img.naturalHeight / img.naturalWidth;
          var w = sampleSize;
          var h = Math.max(1, Math.round(sampleSize * ratio));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          var data = ctx.getImageData(0, 0, w, h).data;
          var top = null;
          var bottom = null;
          for (var y = 0; y < h; y++) {
            var rowHasContent = false;
            for (var x = 0; x < w; x++) {
              if (data[(y * w + x) * 4 + 3] > 10) { rowHasContent = true; break; }
            }
            if (rowHasContent) {
              if (top === null) top = y;
              bottom = y;
            }
          }
          if (top === null) {
            resolve({ topPct: 0, bottomPct: 100 });
          } else {
            resolve({ topPct: (top / h) * 100, bottomPct: (bottom / h) * 100 });
          }
        } catch (e) {
          resolve({ topPct: 0, bottomPct: 100 });
        }
      };
      img.onerror = function () {
        resolve({ topPct: 0, bottomPct: 100 });
      };
      img.src = src;
    });
    contentBoundsCache[src] = promise;
    return promise;
  }

  function loadImageEl(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed to load')); };
      img.src = src;
    });
  }

  function stackLayers(preview) {
    // Each layer is now a wrapper <div class="stack-builder__preview-layer">
    // (so it can also host the drag handle button) with the actual <img>
    // inside it. The negative margin is applied to the wrapper; the image
    // itself is what we sample for transparent padding.
    var layers = Array.prototype.slice.call(preview.querySelectorAll('.stack-builder__preview-layer'));
    if (layers.length === 0) return Promise.resolve();
    var imgs = layers.map(function (layer) { return layer.querySelector('img'); });

    function apply() {
      return Promise.all(imgs.map(function (img) { return getContentBounds(img.src); })).then(function (boundsList) {
        layers.forEach(function (layer, idx) {
          if (idx === 0) {
            layer.style.marginTop = '0';
            return;
          }
          var prevLayer = layers[idx - 1];
          var prevBounds = boundsList[idx - 1];
          var bounds = boundsList[idx];
          var prevHeight = prevLayer.getBoundingClientRect().height || prevLayer.offsetHeight;
          var height = layer.getBoundingClientRect().height || layer.offsetHeight;
          var prevBottomPad = ((100 - prevBounds.bottomPct) / 100) * prevHeight;
          var topPad = (bounds.topPct / 100) * height;
          var overlap = prevBottomPad + topPad + (height * EXTRA_INTERLOCK_RATIO);
          layer.style.marginTop = '-' + Math.max(0, overlap) + 'px';
        });
      });
    }

    var pending = imgs.filter(function (img) { return !img.complete; });
    if (pending.length === 0) {
      return apply();
    }
    return new Promise(function (resolveOuter) {
      var remaining = pending.length;
      pending.forEach(function (img) {
        img.addEventListener('load', function () {
          remaining -= 1;
          if (remaining === 0) apply().then(resolveOuter);
        });
        img.addEventListener('error', function () {
          remaining -= 1;
          if (remaining === 0) apply().then(resolveOuter);
        });
      });
    });
  }

  function initStackBuilder(root) {
    if (root.__stackBuilderInit) return;
    root.__stackBuilderInit = true;

    var config = {};
    try {
      config = JSON.parse(root.getAttribute('data-stack-config') || '{}');
    } catch (e) {
      config = {};
    }
    var labels = config.labels || {};
    var palettes = config.palettes || [];

    var grid = root.querySelector('[data-stack-grid]');
    var filters = root.querySelector('[data-stack-filters]');
    var tabs = root.querySelectorAll('[data-stack-tab]');
    var towersEl = root.querySelector('[data-stack-towers]');
    var activeLabelEl = root.querySelector('[data-stack-active-label]');
    var list = root.querySelector('[data-stack-list]');
    var countEl = root.querySelector('[data-stack-count]');
    var priceEl = root.querySelector('[data-stack-price]');
    var addToCartBtn = root.querySelector('[data-stack-add-to-cart]');
    var addToCartLabel = root.querySelector('[data-stack-add-to-cart-label]');
    var errorEl = root.querySelector('[data-stack-error]');
    var isCompact = root.classList.contains('stack-builder--compact');
    var compactCta = root.querySelector('.stack-builder__compact-cta');
    var toastEl = ensureToast();
    var resetBtn = ensureResetButton();
    var ofTheDayBadge = ensureOfTheDayBadge();
    var mobileBar = ensureMobileBar();
    var mobileThumb = ensureMobileThumb();
    var mobileThumbInner = mobileThumb.querySelector('[data-mobile-thumb-inner]');
    var mobileCountEl = mobileBar.querySelector('[data-mobile-count]');
    var mobilePriceEl = mobileBar.querySelector('[data-mobile-price]');
    var mobileAddBtn = mobileBar.querySelector('[data-mobile-add-btn]');
    var mobileAddLabel = mobileBar.querySelector('[data-mobile-add-label]');
    mobileAddLabel.textContent = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
    var inlineCta = ensureInlineAddButton();
    var inlineAddBtn = inlineCta.querySelector('[data-stack-add-inline]');
    var inlineAddLabel = inlineCta.querySelector('[data-stack-add-inline-label]');
    inlineAddLabel.textContent = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
    var giftWrapCheckbox = root.querySelector('[data-stack-gift-wrap]');
    var giftMessageToggle = root.querySelector('[data-stack-gift-message-toggle]');
    var giftMessageInput = root.querySelector('[data-stack-gift-message-input]');
    var paletteInspiration = root.querySelector('[data-stack-palette-inspiration]');
    var paletteInspirationImg = paletteInspiration ? paletteInspiration.querySelector('[data-stack-palette-inspiration-img]') : null;
    if (giftMessageToggle && giftMessageInput) {
      giftMessageToggle.addEventListener('change', function () {
        giftMessageInput.hidden = !giftMessageToggle.checked;
        if (giftMessageToggle.checked) giftMessageInput.focus();
      });
    }

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-stack-item]')).map(function (el) {
      return {
        el: el,
        moduleId: el.getAttribute('data-module-id'),
        moduleLabel: el.getAttribute('data-module-label'),
        productHandle: el.getAttribute('data-product-handle'),
        role: el.getAttribute('data-role'),
        variantId: el.getAttribute('data-variant-id'),
        color: el.getAttribute('data-color'),
        price: parseFloat(el.getAttribute('data-price')) || 0,
        priceFormatted: el.getAttribute('data-price-formatted'),
        image: el.getAttribute('data-image'),
        available: el.getAttribute('data-available') === 'true'
      };
    });

    // Multiple stacks (full configurator page only — compact/homepage mode
    // never grows past the one initial stack, since the "+ New stack" tile
    // is only ever rendered when !isCompact). Each stack is an independent
    // tower with its own items array; exactly one is "active" at a time —
    // that's the one addItem()/removeStackKey()/moveStackItem()/resetStack()
    // all operate on, and the one whose items populate the sidebar list.
    var stacks = [];
    var activeStackId = null;
    var stackIdCounter = 0;
    var MAX_STACKS = 6;
    // Resolves once the most recent renderTowers() call has finished
    // interlocking every tower (images loaded, negative margins applied).
    // Anything that clones a tower for a snapshot (currently just the
    // "stack of the day" popup) should await this first, otherwise it can
    // clone layers mid-load with no marginTop set yet — they render as
    // separate, un-stacked images instead of one interlocked build.
    var towersReadyPromise = Promise.resolve();

    function newStackId() {
      stackIdCounter += 1;
      return 's' + stackIdCounter;
    }

    function createStack() {
      var s = { id: newStackId(), items: [], isPristineOtd: false };
      stacks.push(s);
      activeStackId = s.id;
      return s;
    }

    function getStack(id) {
      return stacks.filter(function (s) { return s.id === id; })[0];
    }

    function activeStack() {
      return getStack(activeStackId) || stacks[0];
    }

    function activeTowerEl() {
      return towersEl.querySelector('[data-tower-id="' + activeStackId + '"]');
    }

    createStack();

    // In compact mode there's no tab bar (Shapes/Colors are hidden to keep the
    // homepage teaser lightweight), so palettes — the curated, at-a-glance way
    // to browse — become the default and only entry point into the grid.
    var currentTab = (isCompact && palettes.length > 0) ? 'palette' : 'shape';
    var activeFilter = 'all';
    var thumbUpdateToken = 0;

    // "Stack of the day" — instead of an empty grid greeting every visitor,
    // the preview arrives pre-loaded with a small, curated combo so there's
    // always something finished on screen to react to (keep it, remix it, or
    // clear it and start fresh). Picked deterministically from the date so it
    // actually rotates day to day instead of being random on every reload.
    // Only ever seeded into stacks[0] (the first stack); isPristineOtd on
    // that stack object tracks whether it's still exactly that untouched
    // suggestion — it flips to false the moment the shopper does anything
    // (add, remove, reorder, reset), which hides the badge so it never lies
    // about a build the shopper actually made themselves.

    // Fallback used only if the merchant hasn't configured any "Stack of the
    // day" blocks yet in the theme editor (see config.stackOfTheDayPresets,
    // built from those blocks in the section's Liquid). Matches items by
    // moduleLabel rather than product handle, same as the block-driven path,
    // so both sources feed seedStackOfTheDay() identically.
    var DEFAULT_STACK_OF_THE_DAY_PRESETS = [
      { picks: [
        { label: 'Module 1 & 2', color: 'Beige' },
        { label: 'Module 3', color: 'Yellow' },
        { label: 'Module 4', color: 'Bordeaux' },
        { label: 'Module 6', color: 'Bordeaux' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Light Green' },
        { label: 'Module 3', color: 'Dark Green' },
        { label: 'Module 4', color: 'Groen' },
        { label: 'Module 5', color: 'licht groen' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Beige' },
        { label: 'Module 3', color: 'Dark Blue' },
        { label: 'Module 4', color: 'Blauw' },
        { label: 'Module 5', color: 'licht blauw' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Pink' },
        { label: 'Module 3', color: 'Pink' },
        { label: 'Module 4', color: 'Roze' },
        { label: 'Module 6', color: 'Beige' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Brown' },
        { label: 'Module 3', color: 'Brown' },
        { label: 'Module 4', color: 'Bruin' },
        { label: 'Module 5', color: 'Bruin' },
        { label: 'Module 6', color: 'Bruin' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Brown' },
        { label: 'Module 3', color: 'Red' },
        { label: 'Module 4', color: 'Rood' },
        { label: 'Module 5', color: 'Rood' }
      ] },
      { picks: [
        { label: 'Module 1 & 2', color: 'Beige' },
        { label: 'Module 3', color: 'Yellow' },
        { label: 'Module 4', color: 'Geel' },
        { label: 'Module 6', color: 'Geel' }
      ] }
    ];

    // Builds today's preset from the real, currently available items (so a
    // sold-out color just quietly drops out of the combo instead of erroring),
    // in the same shape addItem() would produce, then hands off to render().
    // Presets come from the merchant-editable "Stack of the day" blocks in
    // the theme editor (config.stackOfTheDayPresets) when any are configured;
    // otherwise falls back to the hardcoded defaults above.
    function seedStackOfTheDay() {
      var s = stacks[0];
      if (!s || s.items.length > 0) return;
      var presets = (config.stackOfTheDayPresets && config.stackOfTheDayPresets.length > 0)
        ? config.stackOfTheDayPresets
        : DEFAULT_STACK_OF_THE_DAY_PRESETS;
      var now = new Date();
      var startOfYear = new Date(now.getFullYear(), 0, 0);
      var dayOfYear = Math.floor((now - startOfYear) / 86400000);
      var preset = presets[dayOfYear % presets.length];
      var picks = (preset && preset.picks) || [];
      var seeded = [];
      picks.forEach(function (pick) {
        var match = items.filter(function (i) {
          return i.moduleLabel === pick.label &&
            i.color &&
            i.color.toLowerCase() === String(pick.color).toLowerCase() &&
            i.available;
        })[0];
        if (match) {
          seeded.push(Object.assign({}, match, { key: match.variantId + '-otd-' + seeded.length }));
        }
      });
      if (seeded.length === 0) return;
      var top = seeded.filter(function (x) { return x.role === 'top'; });
      var rest = seeded.filter(function (x) { return x.role !== 'top'; });
      s.items = top.concat(rest);
      s.isPristineOtd = true;
    }

    function updateOfTheDayBadge() {
      if (!ofTheDayBadge) return;
      var s = stacks[0];
      ofTheDayBadge.hidden = !(s && s.isPristineOtd && s.items.length > 0);
    }

    // Drag-to-reposition for the floating tower preview, now free in both
    // directions (not just left/right) since .thumb is position: fixed to
    // the viewport (see the CSS comment on .stack-builder__mobile-bar-thumb).
    // The drag target is .thumb-inner itself — the one element with
    // pointer-events: auto — and dragging is the explicit, reliable way to
    // clear a tile rather than hoping click-through works on every
    // browser/device. Offsets are written as --drag-x/--drag-y custom
    // properties on .thumb, which the CSS folds into its base centering
    // transform via calc(), so centering and dragging share one transform
    // instead of fighting over it.
    var thumbDragX = 0;
    var thumbDragY = 0;

    function initThumbDrag() {
      if (!mobileThumbInner || !mobileThumb) return;
      var pointerId = null;
      var startClientX = 0;
      var startClientY = 0;
      var startDragX = 0;
      var startDragY = 0;
      var minX = 0;
      var maxX = 0;
      var minY = 0;
      var maxY = 0;
      var dragMoved = false;

      mobileThumbInner.addEventListener('pointerdown', function (e) {
        if (typeof e.button === 'number' && e.button !== 0) return;
        pointerId = e.pointerId;
        startClientX = e.clientX;
        startClientY = e.clientY;
        startDragX = thumbDragX;
        startDragY = thumbDragY;
        dragMoved = false;
        // Bound the drag against the tower's own current (painted) rect so
        // it stays fully within the viewport in every direction, with a
        // little breathing room on each side.
        var rect = mobileThumbInner.getBoundingClientRect();
        var margin = 8;
        minX = thumbDragX - (rect.left - margin);
        maxX = thumbDragX + (window.innerWidth - rect.right - margin);
        minY = thumbDragY - (rect.top - margin);
        maxY = thumbDragY + (window.innerHeight - rect.bottom - margin);
        if (maxX < minX) maxX = minX;
        if (maxY < minY) maxY = minY;
        document.addEventListener('pointermove', onThumbDragMove);
        document.addEventListener('pointerup', onThumbDragEnd);
        document.addEventListener('pointercancel', onThumbDragEnd);
      });

      function onThumbDragMove(e) {
        if (e.pointerId !== pointerId) return;
        var dx = e.clientX - startClientX;
        var dy = e.clientY - startClientY;
        if (!dragMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) dragMoved = true;
        if (!dragMoved) return;
        e.preventDefault();
        var nextX = startDragX + dx;
        var nextY = startDragY + dy;
        if (nextX < minX) nextX = minX;
        if (nextX > maxX) nextX = maxX;
        if (nextY < minY) nextY = minY;
        if (nextY > maxY) nextY = maxY;
        thumbDragX = nextX;
        thumbDragY = nextY;
        mobileThumb.style.setProperty('--drag-x', thumbDragX + 'px');
        mobileThumb.style.setProperty('--drag-y', thumbDragY + 'px');
      }

      function onThumbDragEnd(e) {
        if (e.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', onThumbDragMove);
        document.removeEventListener('pointerup', onThumbDragEnd);
        document.removeEventListener('pointercancel', onThumbDragEnd);
        pointerId = null;
      }
    }

    function resetThumbDrag() {
      thumbDragX = 0;
      thumbDragY = 0;
      if (mobileThumb) {
        mobileThumb.style.removeProperty('--drag-x');
        mobileThumb.style.removeProperty('--drag-y');
      }
    }

    function moduleLabels() {
      var seen = {};
      var out = [];
      items.forEach(function (i) {
        if (!seen[i.moduleLabel]) {
          seen[i.moduleLabel] = true;
          out.push(i.moduleLabel);
        }
      });
      return out;
    }

    function colorLabels() {
      var seen = {};
      var out = [];
      items.forEach(function (i) {
        if (i.color && !seen[i.color]) {
          seen[i.color] = true;
          out.push(i.color);
        }
      });
      return out;
    }

    function renderFilters() {
      var options = [];
      if (currentTab === 'shape') {
        options = moduleLabels();
      } else if (currentTab === 'color') {
        options = colorLabels();
      } else if (currentTab === 'palette') {
        options = palettes.map(function (p) { return p.title; });
      }

      activeFilter = 'all';
      var html = '<button type="button" class="stack-builder__filter is-active" data-filter-value="all">' +
        (labels.allModules || 'All modules') + '</button>';
      options.forEach(function (opt) {
        html += '<button type="button" class="stack-builder__filter" data-filter-value="' +
          escapeAttr(opt) + '">' + escapeHtml(opt) + '</button>';
      });
      filters.innerHTML = html;

      filters.querySelectorAll('[data-filter-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          filters.querySelectorAll('.stack-builder__filter').forEach(function (b) {
            b.classList.remove('is-active');
          });
          btn.classList.add('is-active');
          activeFilter = btn.getAttribute('data-filter-value');
          applyFilter();
          updatePaletteInspiration();
        });
      });

      applyFilter();
      updatePaletteInspiration();
    }

    // The inspiration photo is a mood-board image tied to whichever palette
    // is currently selected — only relevant on the Palettes tab, and only
    // once a specific palette (not "All modules") is picked. Falls back to
    // hidden if that palette block has no image configured in the editor.
    function updatePaletteInspiration() {
      if (!paletteInspiration || !paletteInspirationImg) return;
      if (currentTab !== 'palette' || activeFilter === 'all') {
        paletteInspiration.hidden = true;
        return;
      }
      var match = palettes.filter(function (p) { return p.title === activeFilter; })[0];
      if (match && match.image) {
        paletteInspirationImg.src = match.image;
        paletteInspirationImg.alt = match.title || '';
        paletteInspiration.hidden = false;
      } else {
        paletteInspiration.hidden = true;
      }
    }

    function applyFilter() {
      // Palettes are defined as a set of color names (not a product/collection),
      // so a palette can pull in just specific colors of each module shape —
      // e.g. "Earthy tones" = Beige, Brown, Bordeaux — across every module,
      // rather than one module in all of its colors.
      var paletteColors = null;
      if (currentTab === 'palette' && activeFilter !== 'all') {
        var match = palettes.filter(function (p) { return p.title === activeFilter; })[0];
        paletteColors = match ? match.colors.map(function (c) { return String(c).toLowerCase(); }) : [];
      }

      items.forEach(function (i) {
        var visible = true;
        if (activeFilter !== 'all') {
          if (currentTab === 'shape') visible = i.moduleLabel === activeFilter;
          else if (currentTab === 'color') visible = i.color === activeFilter;
          else if (currentTab === 'palette') visible = paletteColors && paletteColors.indexOf(String(i.color || '').toLowerCase()) !== -1;
        }
        i.el.hidden = !visible;
      });
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str == null ? '' : str;
      return div.innerHTML;
    }

    function escapeAttr(str) {
      return String(str == null ? '' : str).replace(/"/g, '&quot;');
    }

    function groupThousands(digits) {
      return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    function formatMoney(cents) {
      // Shopify Liquid prices are integer cents; shop.money_format expects a decimal string
      // and can use any of Shopify's money format tokens (amount, amount_no_decimals,
      // amount_with_comma_separator, amount_no_decimals_with_comma_separator, etc).
      var moneyFormat = config.moneyFormat || '{{amount}} EUR';
      var major = cents / 100;
      var whole = Math.round(major).toString();
      var fixed = major.toFixed(2).split('.'); // [wholePart, decimalPart]

      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
        var useComma = token.indexOf('comma_separator') !== -1;
        var noDecimals = token.indexOf('no_decimals') !== -1;
        if (noDecimals) {
          return useComma ? groupThousands(whole) : whole;
        }
        return useComma ? groupThousands(fixed[0]) + ',' + fixed[1] : fixed[0] + '.' + fixed[1];
      });
    }

    function addItem(item) {
      if (!item.available) return;
      var s = activeStack();
      s.isPristineOtd = false;
      if (item.role === 'top') {
        s.items = s.items.filter(function (x) { return x.role !== 'top'; });
        s.items.unshift(Object.assign({}, item, { key: item.variantId + '-' + Date.now() }));
      } else {
        s.items.push(Object.assign({}, item, { key: item.variantId + '-' + Date.now() + '-' + Math.random() }));
      }
      render();
    }

    function removeStackKey(key) {
      var s = activeStack();
      s.items = s.items.filter(function (x) { return x.key !== key; });
      s.isPristineOtd = false;
      pruneEmptyStacks();
      render();
    }

    // Homepage-to-full-builder handoff (see HANDOFF_KEY comment above).
    // saveHandoff runs on the compact section's "Continue in full builder"
    // link before the browser navigates away; applyHandoffIfAny runs once
    // on the full (non-compact) section's init and replays the saved picks
    // through the normal addItem() path so every existing render/pricing/
    // drag-reorder code just works unmodified.
    function saveHandoff() {
      try {
        var handoffItems = activeStack().items;
        if (handoffItems.length === 0) {
          localStorage.removeItem(HANDOFF_KEY);
          return;
        }
        var payload = {
          ts: Date.now(),
          items: handoffItems.map(function (s) { return { variantId: s.variantId, role: s.role }; })
        };
        localStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
      } catch (e) {
        // localStorage unavailable (private browsing, storage full, etc) —
        // the link still navigates normally, the build just isn't carried over.
      }
    }

    // Returns true only when a genuine homepage build was actually restored —
    // callers use this to decide whether it's still safe to seed the "stack
    // of the day" suggestion (never overwrite a real handoff).
    function applyHandoffIfAny() {
      if (handoffConsumed) return false;
      handoffConsumed = true;
      var raw;
      try {
        raw = localStorage.getItem(HANDOFF_KEY);
      } catch (e) {
        return false;
      }
      if (!raw) return false;
      var payload = null;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        payload = null;
      }
      try {
        localStorage.removeItem(HANDOFF_KEY);
      } catch (e) {
        // ignore
      }
      if (!payload || !payload.items || !payload.items.length) return false;
      if (!payload.ts || (Date.now() - payload.ts) > HANDOFF_TTL_MS) return false;

      var byVariant = {};
      items.forEach(function (i) { byVariant[i.variantId] = i; });

      var applied = false;
      payload.items.forEach(function (saved) {
        var match = byVariant[saved.variantId];
        if (match) {
          addItem(match);
          applied = true;
        }
      });
      if (applied) {
        showToast(labels.handoffRestored || 'Your build from the homepage was carried over');
      }
      return applied;
    }

    // Drag-and-drop reordering, built on Pointer Events so the same code
    // handles mouse AND touch/mobile (native HTML5 drag-and-drop is not
    // reliably supported on touch browsers). Only "stackable" pieces get a
    // handle — the "top" piece always has the wick and always stays on top.
    function clearDropIndicators() {
      root.querySelectorAll('.drag-over-before, .drag-over-after').forEach(function (el) {
        el.classList.remove('drag-over-before', 'drag-over-after');
      });
    }

    function moveStackItem(sourceKey, targetKey, placeAfter) {
      var s = activeStack();
      s.isPristineOtd = false;
      var top = s.items.filter(function (x) { return x.role === 'top'; });
      var rest = s.items.filter(function (x) { return x.role !== 'top'; });
      var fromIndex = -1;
      rest.forEach(function (x, idx) { if (x.key === sourceKey) fromIndex = idx; });
      if (fromIndex === -1) return;
      var moved = rest.splice(fromIndex, 1)[0];
      var toIndex = -1;
      rest.forEach(function (x, idx) { if (x.key === targetKey) toIndex = idx; });
      if (toIndex === -1) {
        rest.push(moved);
      } else {
        rest.splice(placeAfter ? toIndex + 1 : toIndex, 0, moved);
      }
      s.items = top.concat(rest);
      render();
    }

    function findDropTarget(container, itemEl, clientY) {
      var siblings = Array.prototype.filter.call(container.children, function (el) {
        return el !== itemEl && el.hasAttribute('data-drag-key');
      });
      for (var i = 0; i < siblings.length; i++) {
        var rect = siblings[i].getBoundingClientRect();
        if (clientY >= rect.top && clientY <= rect.bottom) {
          return {
            key: siblings[i].getAttribute('data-drag-key'),
            before: (clientY - rect.top) < rect.height / 2
          };
        }
      }
      return null;
    }

    // handleEl: the small grip the user presses on to start dragging.
    // itemEl: the element that actually moves/reorders (li or preview layer).
    // container: the parent whose children are the reorderable siblings.
    function makeDraggable(handleEl, itemEl, key, container) {
      itemEl.setAttribute('data-drag-key', key);
      itemEl.classList.add('is-draggable');
      handleEl.style.touchAction = 'none';

      var pointerId = null;
      var startX = 0;
      var startY = 0;
      var dragging = false;
      var dropTarget = null;

      function onMove(e) {
        if (e.pointerId !== pointerId) return;
        if (!dragging) {
          if (Math.abs(e.clientY - startY) < 6 && Math.abs(e.clientX - startX) < 6) return;
          dragging = true;
          itemEl.classList.add('is-dragging');
        }
        e.preventDefault();
        clearDropIndicators();
        dropTarget = findDropTarget(container, itemEl, e.clientY);
        if (dropTarget) {
          var targetEl = container.querySelector('[data-drag-key="' + dropTarget.key + '"]');
          if (targetEl) targetEl.classList.add(dropTarget.before ? 'drag-over-before' : 'drag-over-after');
        }
      }

      function onUp(e) {
        if (e.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        itemEl.classList.remove('is-dragging');
        clearDropIndicators();
        if (dragging && dropTarget) {
          moveStackItem(key, dropTarget.key, !dropTarget.before);
        }
        dragging = false;
        dropTarget = null;
        pointerId = null;
      }

      handleEl.addEventListener('pointerdown', function (e) {
        if (typeof e.button === 'number' && e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        dragging = false;
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });
    }

    // A clearer "it worked" confirmation than the subtle button label change:
    // a small banner right where the user's attention already is (the
    // preview), and a "Start over" button so an in-progress build can be
    // scrapped in one click instead of removing modules one by one.
    function ensureToast() {
      var existing = root.querySelector('[data-stack-toast]');
      if (existing) return existing;
      var previewWrap = root.querySelector('.stack-builder__preview');
      var toast = document.createElement('div');
      toast.className = 'stack-builder__toast';
      toast.setAttribute('data-stack-toast', '');
      previewWrap.insertBefore(toast, previewWrap.firstChild);
      return toast;
    }

    var toastTimer = null;
    function showToast(message, duration) {
      toastEl.textContent = message;
      toastEl.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toastEl.classList.remove('is-visible');
      }, duration || 3200);
    }

    function ensureResetButton() {
      var existing = root.querySelector('[data-stack-reset]');
      if (existing) return existing;
      var totals = root.querySelector('.stack-builder__totals');
      var countHolder = root.querySelector('[data-stack-count]');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stack-builder__reset';
      btn.setAttribute('data-stack-reset', '');
      btn.hidden = true;
      btn.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M20 4v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '<span>' + escapeHtml(labels.startOver || 'Start over') + '</span>';
      if (countHolder && countHolder.parentNode === totals) {
        totals.insertBefore(btn, countHolder.nextSibling);
      } else {
        totals.appendChild(btn);
      }
      return btn;
    }

    function resetStack() {
      var s = activeStack();
      if (s.items.length === 0) return;
      s.items = [];
      s.isPristineOtd = false;
      pruneEmptyStacks();
      render();
    }

    // Drops any stack left empty by removeStackKey()/resetStack() — but only
    // when it's not the last one left; a single empty stack is normal (the
    // starting state), it just shouldn't be *silently* orphaned when other
    // stacks exist alongside it. Called before render() so the towers row
    // never flashes an empty tile that's about to disappear.
    function pruneEmptyStacks() {
      if (stacks.length <= 1) return;
      var hadActiveId = activeStackId;
      stacks = stacks.filter(function (s) { return s.items.length > 0; });
      if (stacks.length === 0) {
        createStack();
        return;
      }
      if (!getStack(hadActiveId)) activeStackId = stacks[stacks.length - 1].id;
    }

    function addNewStack() {
      if (stacks.length >= MAX_STACKS) return;
      createStack();
      render();
    }

    function removeStack(id) {
      if (stacks.length <= 1) return;
      stacks = stacks.filter(function (s) { return s.id !== id; });
      if (activeStackId === id) activeStackId = stacks[stacks.length - 1].id;
      render();
    }

    function selectStack(id) {
      if (activeStackId === id) return;
      activeStackId = id;
      render();
    }

    // Rebuilds the row of tower tiles — one per stack, plus a "+ New stack"
    // tile on the full configurator page (never in compact mode). Each tower
    // shows its own interlocked preview layers; only the ACTIVE tower's
    // stackable pieces get a drag handle (that's the one addItem()/
    // moveStackItem() actually operate on), and only the active + non-empty
    // tower gets the small download icon.
    function renderTowers() {
      towersEl.innerHTML = '';
      stacks.forEach(function (s, idx) {
        var hasItems = s.items.length > 0;
        var towerEl = document.createElement('div');
        towerEl.className = 'stack-builder__tower' + (hasItems ? ' has-stack' : '') + (s.id === activeStackId ? ' is-active' : '');
        towerEl.setAttribute('data-tower-id', s.id);

        if (!hasItems) {
          var empty = document.createElement('div');
          empty.className = 'stack-builder__empty';
          empty.textContent = labels.emptyText || 'Start by adding a top module';
          towerEl.appendChild(empty);
        } else {
          var top = s.items.filter(function (x) { return x.role === 'top'; });
          var rest = s.items.filter(function (x) { return x.role !== 'top'; });
          var ordered = top.concat(rest);
          ordered.forEach(function (it) {
            var layer = document.createElement('div');
            layer.className = 'stack-builder__preview-layer';

            var img = document.createElement('img');
            img.src = it.image;
            img.alt = it.moduleLabel + ' - ' + it.color;
            img.className = 'stack-builder__preview-layer-img';
            layer.appendChild(img);

            // Only the ACTIVE stack's stackable pieces are draggable — matches
            // the "click a tower to activate it, then edit it" interaction model.
            if (it.role !== 'top' && s.id === activeStackId) {
              var grip = document.createElement('button');
              grip.type = 'button';
              grip.className = 'stack-builder__preview-layer-handle';
              grip.setAttribute('aria-label', labels.reorder || 'Drag to reorder');
              grip.innerHTML = '&#8942;&#8942;';
              layer.appendChild(grip);
              makeDraggable(grip, layer, it.key, towerEl);
            }

            towerEl.appendChild(layer);
          });
        }

        if (stacks.length > 1) {
          var removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'stack-builder__tower-remove';
          removeBtn.setAttribute('data-tower-remove', '');
          removeBtn.setAttribute('aria-label', labels.removeStack || 'Remove this stack');
          removeBtn.innerHTML = '&times;';
          removeBtn.addEventListener('click', function (e) { e.stopPropagation(); removeStack(s.id); });
          towerEl.appendChild(removeBtn);
        }

        var numberTag = document.createElement('span');
        numberTag.className = 'stack-builder__tower-number';
        numberTag.textContent = idx + 1;
        towerEl.appendChild(numberTag);

        // Small download icon, ACTIVE + non-empty tower only (replaces the
        // old persistent ensureDownloadButton() pattern — the button is now
        // rebuilt fresh on every render, scoped to whichever tower is active).
        if (hasItems && s.id === activeStackId) {
          var dl = document.createElement('button');
          dl.type = 'button';
          dl.className = 'stack-builder__download';
          dl.setAttribute('data-stack-download', '');
          dl.setAttribute('aria-label', labels.downloadImage || 'Download image');
          dl.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<path d="M4.5 16.5V18a2.5 2.5 0 0 0 2.5 2.5h10a2.5 2.5 0 0 0 2.5-2.5v-1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>';
          dl.addEventListener('click', function (e) { e.stopPropagation(); downloadStackImage(); });
          towerEl.appendChild(dl);
        }

        towerEl.addEventListener('click', function (e) {
          if (e.target.closest('.stack-builder__preview-layer-handle, [data-tower-remove], [data-stack-download]')) return;
          selectStack(s.id);
        });

        towersEl.appendChild(towerEl);
      });

      if (!isCompact) {
        var addTile = document.createElement('button');
        addTile.type = 'button';
        addTile.className = 'stack-builder__add-stack';
        addTile.setAttribute('data-stack-add-stack', '');
        if (stacks.length >= MAX_STACKS) addTile.disabled = true;
        addTile.innerHTML = '<span class="stack-builder__add-stack-icon">+</span><span>' + escapeHtml(labels.addStack || 'New stack') + '</span>';
        addTile.addEventListener('click', addNewStack);
        towersEl.appendChild(addTile);
      }

      // Tracked so anything that needs a fully-interlocked (margins applied)
      // tower — currently just the "stack of the day" popup — can wait for
      // this specific render's layout work to finish instead of cloning a
      // tower whose images haven't loaded/interlocked yet (see
      // maybeShowOtdModal below, and the towersReadyPromise declaration).
      towersReadyPromise = Promise.all(stacks.map(function (s) {
        var el = towersEl.querySelector('[data-tower-id="' + s.id + '"]');
        return stackLayers(el);
      })).then(function () { updateMobileThumb(); });
    }

    // A small pill next to the preview, only ever visible while the shopper
    // hasn't touched the pre-loaded suggestion yet — see isPristineOtd on
    // stacks[0] (the "stack of the day" is only ever seeded into the first stack).
    function ensureOfTheDayBadge() {
      var existing = root.querySelector('[data-stack-of-the-day]');
      if (existing) return existing;
      var previewWrap = root.querySelector('.stack-builder__preview');
      var badge = document.createElement('div');
      badge.className = 'stack-builder__of-the-day-badge';
      badge.setAttribute('data-stack-of-the-day', '');
      badge.hidden = true;
      badge.textContent = '✨ ' + (labels.stackOfTheDay || 'Stack of the day');
      previewWrap.insertBefore(badge, previewWrap.firstChild);
      return badge;
    }

    // "Stack of the day" popup — a one-per-day, harder-to-miss announcement
    // for the compact/homepage widget specifically (see maybeShowOtdModal
    // below for the isCompact gate). The badge + toast are already there for
    // anyone looking at the widget itself, but a shopper who never scrolls
    // that far would otherwise never learn the feature exists — this makes
    // it unmissable on landing without being an every-visit annoyance.
    // Appended to <body>, same reasoning as the mobile bar/thumb: needs to
    // sit above everything regardless of any transformed ancestor.
    function ensureOtdModal() {
      var existing = document.querySelector('[data-otd-modal]');
      if (existing) return existing;
      var overlay = document.createElement('div');
      overlay.className = 'stack-builder__otd-modal-overlay';
      overlay.setAttribute('data-otd-modal', '');
      overlay.innerHTML =
        '<div class="stack-builder__otd-modal" role="dialog" aria-modal="true">' +
          '<button type="button" class="stack-builder__otd-modal-close" data-otd-modal-close aria-label="Close">&times;</button>' +
          '<div class="stack-builder__otd-modal-badge">✨ <span data-otd-modal-badge-text></span></div>' +
          '<div class="stack-builder__otd-modal-preview" data-otd-modal-preview></div>' +
          '<p class="stack-builder__otd-modal-title" data-otd-modal-title></p>' +
          '<p class="stack-builder__otd-modal-text" data-otd-modal-text></p>' +
          '<button type="button" class="stack-builder__otd-modal-cta" data-otd-modal-cta></button>' +
        '</div>';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeOtdModal();
      });
      overlay.querySelector('[data-otd-modal-close]').addEventListener('click', closeOtdModal);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeOtdModal();
      });
      return overlay;
    }

    function closeOtdModal() {
      var overlay = document.querySelector('[data-otd-modal]');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.classList.remove('stack-builder-otd-modal-open');
    }

    function openOtdModal() {
      var overlay = ensureOtdModal();
      var badgeTextEl = overlay.querySelector('[data-otd-modal-badge-text]');
      var titleEl = overlay.querySelector('[data-otd-modal-title]');
      var textEl = overlay.querySelector('[data-otd-modal-text]');
      var ctaEl = overlay.querySelector('[data-otd-modal-cta]');
      var previewHolder = overlay.querySelector('[data-otd-modal-preview]');

      badgeTextEl.textContent = labels.stackOfTheDay || 'Stack of the day';
      titleEl.textContent = labels.stackOfTheDayModalTitle || 'Today’s Stack of the day is ready';
      textEl.textContent = labels.stackOfTheDayModalText || 'We’ve put together a unique combination for you. Take a look below, or build your own.';
      ctaEl.textContent = labels.stackOfTheDayModalCta || 'View the Stack of the day';

      // Clone the already-rendered, already-interlocked preview (same trick
      // as updateMobileThumb) instead of rebuilding the stack image from
      // scratch — guarantees the popup always matches what's actually on
      // screen, with zero extra image/canvas work.
      previewHolder.innerHTML = '';
      var otdActiveEl = activeTowerEl();
      if (otdActiveEl && otdActiveEl.classList.contains('has-stack')) {
        var clone = otdActiveEl.cloneNode(true);
        clone.querySelectorAll('[data-stack-download], .stack-builder__preview-layer-handle, [data-tower-remove], .stack-builder__tower-number').forEach(function (el) {
          el.remove();
        });
        clone.removeAttribute('data-tower-id');
        clone.removeAttribute('id');
        clone.className = 'stack-builder__otd-modal-preview-stack';
        previewHolder.appendChild(clone);
      }

      ctaEl.onclick = function () {
        closeOtdModal();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      overlay.classList.add('is-visible');
      document.body.classList.add('stack-builder-otd-modal-open');
    }

    // Compact/homepage only, and at most once per calendar day per visitor
    // (tracked in localStorage) so it reads as a "look what's new today"
    // moment rather than a nagging every-visit interruption.
    var OTD_MODAL_STORAGE_KEY = 'candlStackOtdModalDate';
    function maybeShowOtdModal() {
      if (!isCompact) return;
      var todayKey = new Date().toISOString().slice(0, 10);
      var alreadyShownToday;
      try {
        alreadyShownToday = localStorage.getItem(OTD_MODAL_STORAGE_KEY) === todayKey;
      } catch (e) {
        alreadyShownToday = false;
      }
      if (alreadyShownToday) return;
      try {
        localStorage.setItem(OTD_MODAL_STORAGE_KEY, todayKey);
      } catch (e) {
        // localStorage unavailable — worst case the popup shows more than
        // once a day for this visitor, which is harmless.
      }
      // Delay so the popup doesn't compete with the hero/value prop for a
      // visitor's first few seconds on the page (important for cold/paid
      // traffic, who haven't seen the brand before and need to read the
      // headline before anything else grabs their attention). Also wait for
      // towersReadyPromise — on a cold/first load the tower's images may
      // still be loading (and not yet interlocked) at an early mark,
      // especially with a busy hero image competing for bandwidth; cloning
      // too early was producing a popup thumbnail with all the modules
      // floating separately instead of stacked.
      setTimeout(function () {
        towersReadyPromise.then(openOtdModal);
      }, 7000);
    }

    // "Download my stack" — renders the current build onto an off-screen
    // canvas at a nice resolution (independent of the small on-screen
    // preview images) and saves it as a PNG, so a shopper can screenshot
    // and share their creation without an actual OS-level screenshot.
    // (The button itself is now built inline per-render in renderTowers() —
    // no persistent ensureDownloadButton() node anymore, since which tower
    // it belongs to changes as the shopper switches the active stack.)

    // Shopify CDN image URLs carry a `width=` (or `_{w}x`) hint sized for the
    // small on-screen preview; swap in a larger width so the downloaded
    // image is crisp instead of upscaled/blurry.
    function higherResSrc(src, targetWidth) {
      if (!src) return src;
      if (/[?&]width=\d+/.test(src)) {
        return src.replace(/([?&]width=)\d+/, '$1' + targetWidth);
      }
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + targetWidth;
    }

    function buildDownloadCanvas() {
      var activeItems = activeStack().items;
      var top = activeItems.filter(function (s) { return s.role === 'top'; });
      var rest = activeItems.filter(function (s) { return s.role !== 'top'; });
      var ordered = top.concat(rest);
      if (ordered.length === 0) return Promise.reject(new Error('empty stack'));

      var HQ_WIDTH = 1200;
      var TARGET_W = 900;
      var PADDING_X = 140;
      var PADDING_TOP = 90;
      var PADDING_BOTTOM = 130;

      return Promise.all(ordered.map(function (s) {
        var src = higherResSrc(s.image, HQ_WIDTH);
        return Promise.all([loadImageEl(src), getContentBounds(src)]).then(function (res) {
          return { item: s, img: res[0], bounds: res[1] };
        });
      })).then(function (layers) {
        // Scale every layer to the same display width, matching how the
        // live preview shows all modules at one consistent width.
        layers.forEach(function (l) {
          l.dw = TARGET_W;
          l.dh = TARGET_W * (l.img.naturalHeight / l.img.naturalWidth);
        });

        var positions = [];
        var cursorY = 0;
        layers.forEach(function (l, idx) {
          if (idx === 0) {
            l.y = 0;
          } else {
            var prev = layers[idx - 1];
            var prevBottomPad = ((100 - prev.bounds.bottomPct) / 100) * prev.dh;
            var topPad = (l.bounds.topPct / 100) * l.dh;
            var overlap = prevBottomPad + topPad + (l.dh * EXTRA_INTERLOCK_RATIO);
            l.y = prev.y + prev.dh - Math.max(0, overlap);
          }
          positions.push(l.y);
          positions.push(l.y + l.dh);
        });

        var stackTop = Math.min.apply(null, positions);
        var stackBottom = Math.max.apply(null, positions);
        var stackHeight = stackBottom - stackTop;

        var canvasW = TARGET_W + PADDING_X * 2;
        var canvasH = stackHeight + PADDING_TOP + PADDING_BOTTOM;
        var canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        var ctx = canvas.getContext('2d');

        var bgColor = (getComputedStyle(root).getPropertyValue('--sb-bg') || '#f7f5f1').trim() || '#f7f5f1';
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvasW, canvasH);

        var centerX = canvasW / 2;
        var baseY = PADDING_TOP + stackHeight;

        // Soft contact shadow under the base of the stack.
        var shadowW = TARGET_W * 0.85;
        var grad = ctx.createRadialGradient(centerX, baseY, 4, centerX, baseY, shadowW / 2);
        grad.addColorStop(0, 'rgba(20,16,12,0.22)');
        grad.addColorStop(1, 'rgba(20,16,12,0)');
        ctx.save();
        ctx.translate(0, baseY);
        ctx.scale(1, 0.22);
        ctx.translate(0, -baseY);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(centerX, baseY, shadowW / 2, shadowW / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        layers.forEach(function (l) {
          var dx = centerX - l.dw / 2;
          var dy = PADDING_TOP + (l.y - stackTop);
          ctx.drawImage(l.img, dx, dy, l.dw, l.dh);
        });

        ctx.fillStyle = 'rgba(26,26,26,0.55)';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(labels.downloadWatermark || 'Built at staneditions.be', centerX, canvasH - 32);

        return canvas;
      });
    }

    function downloadStackImage() {
      if (activeStack().items.length === 0) return;
      var btn = towersEl.querySelector('[data-stack-download]');
      if (btn) btn.disabled = true;
      buildDownloadCanvas().then(function (canvas) {
        var link = document.createElement('a');
        link.download = 'candl-stack-' + Date.now() + '.png';
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }).catch(function () {
        showToast(labels.downloadError || 'Could not create the image, please try again.');
      }).finally(function () {
        var btnAfter = towersEl.querySelector('[data-stack-download]');
        if (btnAfter) btnAfter.disabled = false;
      });
    }

    // Small circular "now playing"-style snapshot of the current stack for
    // the mobile bar. Rather than rasterizing the stack onto an offscreen
    // canvas (which risks a silently-tainted-canvas failure on some mobile
    // browsers when compositing cross-origin CDN images), this clones the
    // ALREADY-rendered, already-loaded preview DOM — same interlocked image
    // layers, same inline negative margins stackLayers() just computed — and
    // shrinks that clone into the small circle purely with CSS transform.
    // Zero canvas, zero CORS dependency, guaranteed to match what's on screen.
    function updateMobileThumb() {
      thumbUpdateToken += 1;
      var token = thumbUpdateToken;
      var activeElForThumb = activeTowerEl();
      if (!activeElForThumb || !activeElForThumb.classList.contains('has-stack')) {
        mobileThumb.hidden = true;
        mobileThumbInner.innerHTML = '';
        resetThumbDrag();
        return;
      }
      // Give stackLayers() a couple of frames to finish applying its negative
      // margins to the real preview before snapshotting it, so the clone shows
      // pieces properly interlocked instead of just stacked with plain spacing.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (token !== thumbUpdateToken) return;
          var el = activeTowerEl();
          if (!el || !el.classList.contains('has-stack')) return;
          var clone = el.cloneNode(true);
          clone.querySelectorAll('[data-stack-download], .stack-builder__preview-layer-handle, [data-tower-remove], .stack-builder__tower-number').forEach(function (el2) {
            el2.remove();
          });
          clone.removeAttribute('data-tower-id');
          clone.removeAttribute('id');
          clone.className = 'stack-builder__mobile-bar-thumb-stack';
          mobileThumbInner.innerHTML = '';
          mobileThumbInner.appendChild(clone);
          mobileThumb.hidden = false;
        });
      });
    }

    // A fixed bottom bar on mobile mirroring count/price/add-to-cart, so the
    // shopper never has to scroll back up through the grid to check out.
    // Appended to <body> (not the section) so `position: fixed` isn't at the
    // mercy of any transformed ancestor in the theme.
    function ensureMobileBar() {
      var existing = document.querySelector('[data-stack-mobile-bar]');
      if (existing) return existing;
      var bar = document.createElement('div');
      bar.className = 'stack-builder__mobile-bar';
      bar.setAttribute('data-stack-mobile-bar', '');
      bar.innerHTML =
        '<div class="stack-builder__mobile-bar-info">' +
          '<div class="stack-builder__mobile-bar-count" data-mobile-count></div>' +
          '<div class="stack-builder__mobile-bar-price" data-mobile-price></div>' +
        '</div>' +
        '<button type="button" class="stack-builder__mobile-bar-btn" data-mobile-add-btn disabled>' +
          '<span data-mobile-add-label></span>' +
        '</button>';
      document.body.appendChild(bar);
      return bar;
    }

    // The floating tower thumb is a SIBLING of the bar, not a child of it,
    // and that's deliberate: .mobile-bar has its own `transform:
    // translateY(...)` for its slide-up/down show/hide animation, and per
    // the CSS spec, any element with a transform other than `none` becomes
    // the containing block for its position: fixed descendants. Nesting the
    // thumb inside the bar would silently make its "centered on screen"
    // left/top percentages resolve against the BAR's box instead of the
    // real viewport (e.g. "vertically centered" would end up glued near the
    // bottom, where the bar sits) -- exactly the bug this separate element
    // avoids. Appended straight to <body> for the same reason as the bar.
    function ensureMobileThumb() {
      var existing = document.querySelector('[data-mobile-thumb]');
      if (existing) return existing;
      var thumb = document.createElement('div');
      thumb.className = 'stack-builder__mobile-bar-thumb';
      thumb.setAttribute('data-mobile-thumb', '');
      thumb.hidden = true;
      thumb.innerHTML =
        '<div class="stack-builder__mobile-bar-thumb-center">' +
          '<div class="stack-builder__mobile-bar-thumb-inner" data-mobile-thumb-inner></div>' +
        '</div>';
      document.body.appendChild(thumb);
      return thumb;
    }

    // Keep all three "Add to cart" buttons — the original, the inline one,
    // and the mobile fixed bar's — in lockstep for label text and disabled
    // state, without repeating three assignments everywhere addToCart
    // touches them.
    // A second "Add to cart" sitting right under the running total, right
    // after the preview. On desktop the preview column is sticky, so this
    // rides along while the module grid scrolls by; on mobile the fixed
    // bottom bar backs it up once it scrolls out of view. This is also the
    // one and only "Add to cart" shown in compact mode, alongside the free
    // gift-wrap / gift-message options (kept here, rather than duplicated
    // per button, since every "Add to cart" trigger reads from this single
    // pair of checkboxes at click time regardless of which button fired it).
    function ensureInlineAddButton() {
      var existing = root.querySelector('[data-stack-add-inline]');
      if (existing) return existing.closest('.stack-builder__inline-cta');
      var totals = root.querySelector('.stack-builder__totals');
      var wrap = document.createElement('div');
      wrap.className = 'stack-builder__inline-cta';
      wrap.innerHTML =
        '<div class="stack-builder__gift-options" data-stack-gift-options>' +
          '<label class="stack-builder__gift-option">' +
            '<input type="checkbox" data-stack-gift-wrap>' +
            '<span>' + escapeHtml(labels.giftWrapLabel || 'Add free gift wrapping') + '</span>' +
          '</label>' +
          '<label class="stack-builder__gift-option">' +
            '<input type="checkbox" data-stack-gift-message-toggle>' +
            '<span>' + escapeHtml(labels.giftMessageLabel || 'Add a free personal message card') + '</span>' +
          '</label>' +
          '<textarea class="stack-builder__gift-message-input" data-stack-gift-message-input rows="2" placeholder="' + escapeAttr(labels.giftMessagePlaceholder || 'Write your message…') + '" hidden></textarea>' +
        '</div>' +
        '<button type="button" class="stack-builder__add-to-cart" data-stack-add-inline disabled>' +
          '<span data-stack-add-inline-label></span>' +
        '</button>';
      totals.parentNode.insertBefore(wrap, totals.nextSibling);
      return wrap;
    }

    function setAddLabel(text) {
      if (addToCartLabel) addToCartLabel.textContent = text;
      mobileAddLabel.textContent = text;
      inlineAddLabel.textContent = text;
    }

    function setAddDisabled(disabled) {
      if (addToCartBtn) addToCartBtn.disabled = disabled;
      mobileAddBtn.disabled = disabled;
      inlineAddBtn.disabled = disabled;
    }

    function render() {
      renderTowers();

      // Sidebar list: only the ACTIVE stack's items (top piece first, then
      // stackable pieces in selection order underneath) — matches the tower
      // that's currently draggable/editable in the preview row above.
      var active = activeStack();
      var top = active.items.filter(function (s) { return s.role === 'top'; });
      var rest = active.items.filter(function (s) { return s.role !== 'top'; });
      var ordered = top.concat(rest);

      list.innerHTML = '';
      ordered.forEach(function (s) {
        var li = document.createElement('li');
        li.className = 'stack-builder__list-item';
        var handleMarkup = s.role !== 'top'
          ? '<button type="button" class="stack-builder__list-item-handle" aria-label="' + escapeAttr(labels.reorder || 'Drag to reorder') + '">&#8942;&#8942;</button>'
          : '<span class="stack-builder__list-item-handle stack-builder__list-item-handle--locked" aria-hidden="true"></span>';
        li.innerHTML = handleMarkup +
          '<span class="stack-builder__list-item-text">' + escapeHtml(s.moduleLabel) + ' — ' + escapeHtml(s.color) + '</span>' +
          '<span class="stack-builder__list-item-price">' + escapeHtml(s.priceFormatted) + '</span>' +
          '<button type="button" class="stack-builder__list-item-remove" aria-label="' + escapeAttr(labels.remove || 'Remove') + '">&times;</button>';
        li.querySelector('.stack-builder__list-item-remove').addEventListener('click', function () {
          removeStackKey(s.key);
        });
        if (s.role !== 'top') {
          makeDraggable(li.querySelector('.stack-builder__list-item-handle'), li, s.key, list);
        }
        list.appendChild(li);
      });

      if (stacks.length > 1) {
        activeLabelEl.hidden = false;
        activeLabelEl.textContent = (labels.editingStack || 'Editing Stack {n}').replace('{n}', String(stacks.indexOf(active) + 1));
      } else {
        activeLabelEl.hidden = true;
      }

      // Badges on grid items showing how many times each variant is currently
      // used, summed across ALL stacks (not just the active one) — a color
      // already used in another tower still shows its running count here.
      var counts = {};
      stacks.forEach(function (st) {
        st.items.forEach(function (it) { counts[it.variantId] = (counts[it.variantId] || 0) + 1; });
      });
      items.forEach(function (i) {
        var badge = i.el.querySelector('[data-item-badge]');
        var n = counts[i.variantId] || 0;
        if (n > 0) {
          badge.hidden = false;
          badge.textContent = n;
          i.el.classList.add('is-selected');
        } else {
          badge.hidden = true;
          i.el.classList.remove('is-selected');
        }
      });

      // Totals across ALL stacks.
      var allItems = [];
      stacks.forEach(function (st) { allItems = allItems.concat(st.items); });
      var nonEmptyCount = stacks.filter(function (st) { return st.items.length > 0; }).length;
      var total = allItems.reduce(function (sum, s) { return sum + s.price; }, 0);
      var countText = allItems.length + (allItems.length === 1 ? ' module' : ' modules');
      if (nonEmptyCount > 1) {
        countText += ' · ' + nonEmptyCount + ' stacks';
      }
      countEl.textContent = countText;
      priceEl.textContent = allItems.length ? formatMoney(total) : '';
      mobileCountEl.textContent = countEl.textContent;
      mobilePriceEl.textContent = priceEl.textContent;
      mobileBar.classList.toggle('is-active', allItems.length > 0);

      setAddDisabled(allItems.length === 0);
      resetBtn.hidden = active.items.length === 0;
      updateOfTheDayBadge();
    }

    // Each build added to the cart gets a short, human-readable label like
    // "Creation 1", "Creation 2"... All line items from the same "Add to
    // cart" click share that exact label as a property, which (a) forces
    // Shopify to keep them as separate lines instead of merging quantities
    // across different towers, and (b) lets you spot at a glance, both in
    // the cart and in the order, which modules belong to which tower —
    // no hidden IDs, the label itself is the grouping key.
    var CREATION_PREFIX = labels.creationPrefix || 'Creation';
    var creationNumber = 1;
    var creationsPanel = ensureCreationsPanel();

    function fetchCart() {
      return fetch((config.cartUrl || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); });
    }

    function parseCreationNumber(label) {
      var match = /(\d+)\s*$/.exec(String(label || ''));
      return match ? parseInt(match[1], 10) : null;
    }

    function ensureCreationsPanel() {
      var existing = root.querySelector('[data-stack-creations]');
      if (existing) return existing;
      var picker = root.querySelector('.stack-builder__picker') || errorEl.parentNode;
      var panel = document.createElement('div');
      panel.className = 'stack-builder__creations';
      panel.setAttribute('data-stack-creations', '');
      panel.hidden = true;
      panel.innerHTML = '<div class="stack-builder__creations-title">' +
        escapeHtml(labels.creationsTitle || 'Your creations in this cart') + '</div>' +
        '<ul class="stack-builder__creations-list" data-stack-creations-list></ul>' +
        '<a class="stack-builder__creations-view-cart" data-stack-creations-cart href="' + escapeAttr(config.cartUrl || '/cart') + '">' +
        escapeHtml(labels.viewCart || 'View cart') + '</a>';
      picker.insertBefore(panel, errorEl.nextSibling);
      return panel;
    }

    function refreshCreations() {
      fetchCart().then(function (cart) {
        var groups = {};
        var order = [];
        (cart.items || []).forEach(function (item) {
          var props = item.properties || {};
          var label = props['Creation'];
          if (!label) return;
          if (!groups[label]) {
            groups[label] = { label: label, build: props['Build'] || '', total: 0, count: 0 };
            order.push(label);
          }
          groups[label].total += item.line_price;
          groups[label].count += item.quantity;
        });

        var maxN = 0;
        order.forEach(function (label) {
          var n = parseCreationNumber(label);
          if (n && n > maxN) maxN = n;
        });
        creationNumber = maxN + 1;

        order.sort(function (a, b) {
          return (parseCreationNumber(a) || 0) - (parseCreationNumber(b) || 0);
        });

        var listEl = creationsPanel.querySelector('[data-stack-creations-list]');
        listEl.innerHTML = '';
        order.forEach(function (label) {
          var g = groups[label];
          var li = document.createElement('li');
          li.className = 'stack-builder__creations-item';
          li.innerHTML = '<span class="stack-builder__creations-item-label">' + escapeHtml(label) + '</span>' +
            '<span class="stack-builder__creations-item-recipe">' + escapeHtml(g.build) + '</span>' +
            '<span class="stack-builder__creations-item-price">' + escapeHtml(formatMoney(g.total)) + '</span>';
          listEl.appendChild(li);
        });
        creationsPanel.hidden = order.length === 0;
      }).catch(function () { /* non-critical, ignore */ });
    }

    refreshCreations();

    function addToCart() {
      // Every non-empty stack goes in as its own "Creation" in a single
      // combined /cart/add.js request — one "Add to cart" click, N creations.
      var stacksToSubmit = stacks.filter(function (s) { return s.items.length > 0; });
      if (stacksToSubmit.length === 0) return;
      errorEl.hidden = true;
      setAddDisabled(true);
      var originalLabel = addToCartLabel ? addToCartLabel.textContent : (labels.addToCart || '');
      setAddLabel(labels.adding || 'Adding…');

      // Free gift options (wrapping / personal message) are opt-in checkboxes
      // shared by the whole click, not tied to any single stack — computed
      // once here and applied identically to every stack's line items below.
      // No separate $0 products needed; this keeps checkout and inventory simple.
      var giftProps = {};
      if (giftWrapCheckbox && giftWrapCheckbox.checked) {
        giftProps['Gift wrapping'] = 'Yes, please';
      }
      if (giftMessageToggle && giftMessageToggle.checked) {
        var giftMessageValue = giftMessageInput ? giftMessageInput.value.trim() : '';
        giftProps['Gift message'] = giftMessageValue || 'Yes, please (no message provided)';
      }

      // Each stack in this batch gets its own "Creation N" label, incrementing
      // locally from the current creationNumber so multiple stacks submitted
      // in the same click never collide with each other. refreshCreations()
      // re-derives the authoritative next creationNumber from the live cart
      // once the request succeeds, same as it always has for a single stack.
      var allCartItems = [];
      var creationLabels = [];
      stacksToSubmit.forEach(function (s, idx) {
        var creationLabel = CREATION_PREFIX + ' ' + (creationNumber + idx);
        creationLabels.push(creationLabel);
        var buildDescription = s.items.map(function (it) { return it.moduleLabel + ' (' + it.color + ')'; }).join(' + ');
        var grouped = {};
        s.items.forEach(function (it) {
          grouped[it.variantId] = (grouped[it.variantId] || 0) + 1;
        });
        Object.keys(grouped).forEach(function (variantId) {
          var itemProperties = {
            'Creation': creationLabel,
            'Build': buildDescription
          };
          Object.keys(giftProps).forEach(function (key) {
            itemProperties[key] = giftProps[key];
          });
          allCartItems.push({
            id: variantId,
            quantity: grouped[variantId],
            properties: itemProperties
          });
        });
      });

      var payload = { items: allCartItems };

      fetch(config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          setAddLabel(labels.added || 'Added to cart');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:update'));
          var drawer = document.querySelector('cart-drawer, cart-notification, #cart-drawer, #CartDrawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          }
          stacks = [];
          createStack();
          if (giftWrapCheckbox) giftWrapCheckbox.checked = false;
          if (giftMessageToggle) giftMessageToggle.checked = false;
          if (giftMessageInput) {
            giftMessageInput.value = '';
            giftMessageInput.hidden = true;
          }
          render();
          refreshCreations();
          if (stacksToSubmit.length === 1) {
            showToast('✓ ' + creationLabels[0] + ' ' + (labels.addedSuffix || 'added to your cart'));
          } else {
            showToast('✓ ' + stacksToSubmit.length + ' ' + (labels.multipleAddedSuffix || 'stacks added to your cart'));
          }
          setTimeout(function () { setAddLabel(originalLabel); }, 2500);
        })
        .catch(function (err) {
          errorEl.hidden = false;
          errorEl.textContent = (err && (err.description || err.message)) || labels.genericError || 'Something went wrong, please try again.';
          setAddLabel(originalLabel);
        })
        .finally(function () {
          setAddDisabled(stacks.every(function (s) { return s.items.length === 0; }));
        });
    }

    items.forEach(function (i) {
      i.el.addEventListener('click', function () { addItem(i); });
    });

    tabs.forEach(function (tabBtn) {
      tabBtn.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tabBtn.classList.add('is-active');
        tabBtn.setAttribute('aria-selected', 'true');
        currentTab = tabBtn.getAttribute('data-stack-tab');
        renderFilters();
      });
    });

    if (addToCartBtn) addToCartBtn.addEventListener('click', addToCart);
    mobileAddBtn.addEventListener('click', addToCart);
    inlineAddBtn.addEventListener('click', addToCart);
    resetBtn.addEventListener('click', resetStack);
    if (compactCta) compactCta.addEventListener('click', saveHandoff);

    initThumbDrag();
    renderFilters();
    render();
    // Homepage-to-full-builder handoff only makes sense on the full page
    // (that's where a saved localStorage build gets replayed) — the compact
    // homepage widget is the one that WRITES the handoff, never reads it
    // back, so it never has anything to restore.
    var hadHandoff = isCompact ? false : applyHandoffIfAny();
    if (!hadHandoff) {
      // No real build to restore — greet the shopper with today's curated
      // suggestion instead of an empty grid. The badge (see
      // ensureOfTheDayBadge) stays up as a persistent label for as long as
      // the suggestion is untouched; this toast is the one-time explainer
      // that fades on its own, so a first-time visitor understands WHY a
      // tower is already sitting there and that it's theirs to change.
      // Runs in compact mode too (e.g. the homepage teaser), not just the
      // full configurator page, so the "stack of the day" is visible
      // wherever the widget appears.
      seedStackOfTheDay();
      if (stacks[0].items.length > 0) {
        render();
        // Duration is a plain-language setting in the theme editor ("...
        // duration (seconds)") so the merchant can lengthen or shorten it
        // themselves without needing a code change.
        var introSeconds = Number(config.stackOfTheDayIntroDuration) || 6;
        showToast(
          labels.stackOfTheDayIntro || 'We built today’s Stack of the day for you — customize it, or hit “Start over” to build your own.',
          introSeconds * 1000
        );
        // Homepage-only, once-per-day popup — see maybeShowOtdModal() for
        // why this doesn't also fire on the full configurator page.
        maybeShowOtdModal();
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-stack-builder]').forEach(initStackBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-stack-builder]');
    if (root) initStackBuilder(root);
  });
})();
